import type { Quote } from '@/types';
import { hasOutdatedRows, isExpiringSoon, isFullySigned, needsUboCountersign } from './quoteSignals';

/**
 * De secundaire filters achter de knop 'Meer filters'.
 *
 * De filterbalk liet eerder alles tegelijk zien, waardoor de zoekbalk in de
 * verdrukking kwam. Wat je zelden aanzet hoort achter één knop met een teller,
 * zodat je wel kunt zien dát er iets aanstaat.
 */
export interface QuoteAdvancedFilters {
  /** Standaard aan: spookoffertes vervuilen het conceptoverzicht niet. */
  hideGhosts: boolean;
  awaitingUbo: boolean;
  fullySigned: boolean;
  expiry: 'all' | 'soon' | 'expired';
  /** Bedragen in hele euro's zoals ingetypt; leeg = geen grens. */
  minValue: string;
  maxValue: string;
  outdatedOnly: boolean;
}

export const DEFAULT_ADVANCED_FILTERS: QuoteAdvancedFilters = {
  hideGhosts: true,
  awaitingUbo: false,
  fullySigned: false,
  expiry: 'all',
  minValue: '',
  maxValue: '',
  outdatedOnly: false,
};

const parseEuros = (value: string) => {
  const cleaned = value.replace(/[^\d]/g, '');
  return cleaned ? Number(cleaned) * 100 : null;
};

export function describeAdvancedFilters(filters: QuoteAdvancedFilters) {
  const active: { key: keyof QuoteAdvancedFilters; label: string }[] = [];
  if (!filters.hideGhosts) active.push({ key: 'hideGhosts', label: 'Spookoffertes zichtbaar' });
  if (filters.awaitingUbo) active.push({ key: 'awaitingUbo', label: 'Wacht op UBO' });
  if (filters.fullySigned) active.push({ key: 'fullySigned', label: 'Volledig ondertekend' });
  if (filters.expiry === 'soon') active.push({ key: 'expiry', label: 'Verloopt binnen 7 dagen' });
  if (filters.expiry === 'expired') active.push({ key: 'expiry', label: 'Verlopen' });
  if (filters.minValue.trim()) active.push({ key: 'minValue', label: `Vanaf € ${filters.minValue.trim()}` });
  if (filters.maxValue.trim()) active.push({ key: 'maxValue', label: `Tot € ${filters.maxValue.trim()}` });
  if (filters.outdatedOnly) active.push({ key: 'outdatedOnly', label: 'Verouderde productversie' });
  return active;
}

export function clearAdvancedFilter(
  filters: QuoteAdvancedFilters,
  key: keyof QuoteAdvancedFilters,
): QuoteAdvancedFilters {
  return { ...filters, [key]: DEFAULT_ADVANCED_FILTERS[key] };
}

export function countAdvancedFilters(filters: QuoteAdvancedFilters) {
  return describeAdvancedFilters(filters).length;
}

export function matchesAdvancedFilters(
  quote: Quote,
  filters: QuoteAdvancedFilters,
  now: number = Date.now(),
) {
  if (filters.awaitingUbo && !needsUboCountersign(quote)) return false;
  if (filters.fullySigned && !isFullySigned(quote)) return false;

  if (filters.expiry === 'soon' && !isExpiringSoon(quote, now)) return false;
  if (filters.expiry === 'expired') {
    const expiresAt = quote.expiresAt ? new Date(quote.expiresAt).getTime() : null;
    if (expiresAt === null || expiresAt >= now) return false;
  }

  const min = parseEuros(filters.minValue);
  const max = parseEuros(filters.maxValue);
  if (min !== null && (quote.valueCents ?? 0) < min) return false;
  if (max !== null && (quote.valueCents ?? 0) > max) return false;

  if (filters.outdatedOnly && !hasOutdatedRows(quote)) return false;

  return true;
}
